import { useContext, useRef, useState } from 'react'

import { useSWRInfinite } from 'swr'

import { fetchPagedData, MetricsApi, PagedResponse } from 'api'
import { DateRange } from 'components/DateRangePicker'
import { CurrentUserContext } from 'context/currentUserContext'
import { SortContext } from 'context/sortContext'
import { AccountActivity, AccountActivitySortValue } from 'modules/accountActivity/domain'
import { FilterPage } from 'utils/filters'

import {
  getCurrentState,
  PagedResponseData,
  setResultSetSize,
  useTransformedFilters
} from './paginationAndApiUtils'

let loadTimeMetricSent = false

const getBaseUrl = (index: number, pageData: Record<string, any> | null) => {
  const baseUrl = '/v2/accounts/search_with_activities/?pageSize=25'
  return index > 0 ? pageData?.nextPagePath || baseUrl : baseUrl
}

export const useAccounts = (): PagedResponseData<AccountActivity> => {
  const [resultSize, setResultSize] = useState<number | undefined>()

  const currentUser = useContext(CurrentUserContext)

  const { accountsSort, accountsDescending } = useContext(SortContext)

  const { searchQuery, dateRange, serializableFilterGroups } = useTransformedFilters(
    FilterPage.ACCOUNTS,
    currentUser?.id
  )

  const timeOfInitialRender = useRef(window.performance?.now())

  const { data, error, mutate, size, setSize } = useSWRInfinite<PagedResponse<AccountActivity>>(
    (index: number, previousPageData: PagedResponse<AccountActivity> | null) => {
      if (!currentUser) return null
      if (previousPageData && !previousPageData.nextPagePath) return null

      const baseUrl = getBaseUrl(index, previousPageData)
      const serializedFilterGroups = JSON.stringify(serializableFilterGroups)

      return [
        baseUrl,
        accountsSort,
        accountsDescending,
        searchQuery,
        dateRange,
        serializedFilterGroups
      ]
    },
    (
      url: string,
      accountsSort: AccountActivitySortValue | undefined,
      accountsDescending: boolean | undefined,
      searchQuery: string | undefined,
      dateRange: DateRange,
      stringifiedFilterGroups: string
    ) => {
      return fetchPagedData<AccountActivity>({
        url,
        sortBy: accountsSort,
        descending: accountsDescending,
        accountName: searchQuery,
        dateRange: dateRange,
        filterGroups: JSON.parse(stringifiedFilterGroups)
      })
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      loadingTimeout: 30_000,
      onSuccess: () => {
        if (!loadTimeMetricSent) {
          MetricsApi.post('/monitoring/metrics/add', {
            value: window.performance.now() - timeOfInitialRender.current,
            metric: 'target-accounts-first-rows-displayed',
            dimensions: {
              type: 'bucket',
              buckets: '500,1000,2000,4000,8000'
            }
          })

          loadTimeMetricSent = true
        }
      },
      onLoadingSlow: () => {
        MetricsApi.post('/monitoring/metrics/add', {
          metric: `target-accounts-loading-slow`,
          value: 1,
          dimensions: {
            type: 'counter'
          }
        })
      },
      onError: () => {
        MetricsApi.post('/monitoring/metrics/add', {
          metric: `target-accounts-error`,
          value: 1,
          dimensions: {
            type: 'counter'
          }
        })
      }
    }
  )

  setResultSetSize<AccountActivity>(setResultSize, data, resultSize)

  return {
    ...getCurrentState<AccountActivity>(data, error, size),
    resultSize,
    error,
    mutate,
    size,
    setSize
  }
}
